import type { CaseShowcaseFixture, CaseShowcaseRecord } from './caseShowcase';

type CasePlanRelations = Record<string, string[]>;

const RELATIONS_STORAGE_KEY = 'cpq-v2.case-showcase.plan-relations.mock.v1';
const CASES_STORAGE_KEY = 'cpq-v2.case-showcase.cases.mock.v1';
const CART_STORAGE_KEY = 'cpq-v2.case-showcase.cart.mock.v1';

export interface CaseShowcaseMockCartEntry {
  caseRecordId: string;
  planId: string;
}

/** Reads the case rehearsal cart from the current browser session. */
export function readCaseShowcaseMockCart(): CaseShowcaseMockCartEntry[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const stored: unknown = JSON.parse(raw);
    if (!Array.isArray(stored)) return [];
    return stored.filter(
      (entry): entry is CaseShowcaseMockCartEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as CaseShowcaseMockCartEntry).caseRecordId === 'string' &&
        typeof (entry as CaseShowcaseMockCartEntry).planId === 'string',
    );
  } catch {
    return [];
  }
}

/** Saves the case rehearsal cart for the current browser session. */
export function saveCaseShowcaseMockCart(
  entries: readonly CaseShowcaseMockCartEntry[],
) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(entries));
}

function isCaseShowcaseRecord(value: unknown): value is CaseShowcaseRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<CaseShowcaseRecord>;
  return (
    typeof record.id === 'string' &&
    typeof record.caseId === 'string' &&
    typeof record.name === 'string' &&
    typeof record.heroImage === 'string' &&
    Array.isArray(record.gallery) &&
    Array.isArray(record.planIds)
  );
}

interface CaseShowcaseMockState {
  v: 2;
  cases: CaseShowcaseRecord[];
  relations: CasePlanRelations;
}

function readStoredCases(): unknown {
  try {
    const raw = window.sessionStorage.getItem(CASES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isCaseShowcaseMockState(
  value: unknown,
): value is CaseShowcaseMockState {
  if (typeof value !== 'object' || value === null) return false;
  const state = value as Partial<CaseShowcaseMockState>;
  return (
    state.v === 2 &&
    Array.isArray(state.cases) &&
    state.cases.every(isCaseShowcaseRecord) &&
    typeof state.relations === 'object' &&
    state.relations !== null &&
    !Array.isArray(state.relations)
  );
}

/** Reads saved Mock cases; only a missing/invalid value falls back to the fixture. */
export function readCaseShowcaseMockCases(
  fixture: CaseShowcaseFixture,
): CaseShowcaseRecord[] {
  if (typeof window === 'undefined') return fixture.cases;

  try {
    const stored = readStoredCases();
    if (isCaseShowcaseMockState(stored)) return stored.cases;
    if (!Array.isArray(stored)) return fixture.cases;
    const records = stored.filter(isCaseShowcaseRecord);
    return stored.length === 0 || records.length > 0 ? records : fixture.cases;
  } catch {
    return fixture.cases;
  }
}

/** One storage write commits cases and their links together, accepting existing v1 values on read. */
export function saveCaseShowcaseMockState(
  cases: readonly CaseShowcaseRecord[],
  relations: CasePlanRelations,
) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    CASES_STORAGE_KEY,
    JSON.stringify({ v: 2, cases, relations }),
  );
}

/** Saves disposable frontend Mock cases; this module never calls a formal API. */
export function saveCaseShowcaseMockCases(
  cases: readonly CaseShowcaseRecord[],
) {
  if (typeof window === 'undefined') return;
  const stored = readStoredCases();
  if (isCaseShowcaseMockState(stored)) {
    saveCaseShowcaseMockState(cases, stored.relations);
    return;
  }
  window.sessionStorage.setItem(CASES_STORAGE_KEY, JSON.stringify(cases));
}

/** Reads case-to-plan links from the current browser session, falling back to the fixture. */
export function readCaseShowcaseMockPlanRelations(
  fixture: CaseShowcaseFixture,
): CasePlanRelations {
  const cases = readCaseShowcaseMockCases(fixture);
  const relations = Object.fromEntries(
    cases.map((record) => [record.id, [...record.planIds]]),
  );

  if (typeof window === 'undefined') return relations;

  try {
    const savedCases = readStoredCases();
    let stored: unknown;
    if (isCaseShowcaseMockState(savedCases)) {
      stored = savedCases.relations;
    } else {
      const raw = window.sessionStorage.getItem(RELATIONS_STORAGE_KEY);
      stored = raw ? JSON.parse(raw) : null;
    }
    if (typeof stored !== 'object' || stored === null) return relations;

    const validPlanIds = new Set(fixture.catalog.plans.map((plan) => plan.id));
    for (const record of cases) {
      const planIds = (stored as Record<string, unknown>)[record.id];
      if (!Array.isArray(planIds)) continue;
      relations[record.id] = [
        ...new Set(
          planIds.filter(
            (planId): planId is string =>
              typeof planId === 'string' && validPlanIds.has(planId),
          ),
        ),
      ];
    }
  } catch {
    return relations;
  }

  return relations;
}

/** Saves only disposable frontend Mock links; this module never calls a formal API. */
export function saveCaseShowcaseMockPlanRelations(
  relations: CasePlanRelations,
) {
  if (typeof window === 'undefined') return;
  const stored = readStoredCases();
  if (isCaseShowcaseMockState(stored)) {
    saveCaseShowcaseMockState(stored.cases, relations);
    return;
  }
  window.sessionStorage.setItem(
    RELATIONS_STORAGE_KEY,
    JSON.stringify(relations),
  );
}
